import type { ColDef } from 'ag-grid-community';
import React, { useEffect, useCallback, useMemo } from 'react';
import ReactTooltip from 'react-tooltip';
import debounce from 'lodash/debounce';
import classnames from 'classnames';

import AgGrid from '../../shared/agGrid/agGrid.component';
import appendCustomRenderer from './withIndicatorCellRenderer';

import { importValidationColumnDefinition, addUpdateColumn } from '../../shared/columnDefinitions';

const rebuildReactTooltip = debounce(ReactTooltip.rebuild, 200);

const appendValueGetter = (columnDefs: ColDef[]) => columnDefs.map((columnDef): ColDef => ({
  ...columnDef,
  valueGetter: params => params.data[params.colDef.field!] ? params.data[params.colDef.field!].value : null,
}));

type RowData = Record<string, unknown> & {validationData?: { value: Record<string, { message: string }[]> }}

type ImportGridProps = {
  data: RowData[],
  hasValidationErrors?: boolean,
  columnDefs: ColDef[],
  className?: string,
  isLoading?: boolean,
}

const ImportGrid = ({
  data,
  hasValidationErrors,
  columnDefs,
  className,
  isLoading = false,
}: ImportGridProps) => {
  useEffect(() => rebuildReactTooltip(), [data]);

  const onGridReady = useCallback(
    ({ api }) => {
      api.addEventListener('bodyScroll', rebuildReactTooltip);
    },
    []
  );

  const onRowDataUpdated = useCallback(
    ({ gridApi }) => {
      if (gridApi) {
        gridApi?.applyColumnState({
          state: [
            {
              colId: importValidationColumnDefinition.field,
              sort: 'asc',
            },
          ],
        });
      }
    },
    []
  );

  const rowData = useMemo(() => data.map(entity => {
    if (entity.validationData) {
      entity.IMPORT_VALIDATION = Object.values(entity.validationData.value).flat().map(error => error.message).join(', ');
    }
    return entity;
  }), [data]);

  const columnDefinitions = useMemo(() => [
    ...(hasValidationErrors ? [importValidationColumnDefinition] : []),
    ...(data && data.length
      ? [addUpdateColumn]
      : []
    ),
    ...appendValueGetter(appendCustomRenderer(columnDefs)),
  ], [data, hasValidationErrors, columnDefs]);

  return (
    <div className={classnames(className, 'page-content-container')}>
      <div className="ag-theme-bootstrap">
        <AgGrid
          columnDefs={columnDefinitions}
          rowData={rowData}
          withSearchBar
          onGridReady={onGridReady}
          onRowDataUpdated={onRowDataUpdated}
          isGridLoading={isLoading}
        />
        <ReactTooltip place="right" effect="float" />
      </div>
    </div>
  );
};

export default ImportGrid;
